/**
 * API клиент для Worker App
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

// Проверка валидности API_BASE_URL
console.log('[apiClient] API_BASE_URL:', API_BASE_URL)
if (!API_BASE_URL || typeof API_BASE_URL !== 'string') {
  console.warn('[apiClient] VITE_API_BASE_URL не настроен, используется значение по умолчанию')
}

// ВАЖНО: Если в production используется localhost - это ошибка конфигурации
if (API_BASE_URL.includes('localhost') && window.location.hostname !== 'localhost') {
  console.error('🚨 [apiClient] КРИТИЧЕСКАЯ ОШИБКА: API_BASE_URL указывает на localhost, но приложение запущено на', window.location.hostname)
  console.error('🚨 [apiClient] Проверьте, что VITE_API_BASE_URL передан в Docker build через --build-arg')
}

export interface ApiError {
  detail: string
  code?: string
}

export class ApiClientError extends Error {
  statusCode?: number
  code?: string
  
  constructor(
    message: string,
    statusCode?: number,
    code?: string
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.statusCode = statusCode
    this.code = code
  }
}

async function handleResponse<T>(response: Response, isEmployeeRequest: boolean = false): Promise<T> {
  if (!response.ok) {
    let errorData: ApiError
    try {
      errorData = await response.json()
    } catch {
      errorData = { detail: response.statusText }
    }
    
    // Если это запрос к employee endpoint и получен 404 - сотрудник удален
    if (isEmployeeRequest && response.status === 404) {
      const { EmployeeDeletedError } = await import('../errors/EmployeeDeletedError')
      throw new EmployeeDeletedError(errorData.detail || 'Employee not found')
    }
    
    throw new ApiClientError(
      errorData.detail || response.statusText,
      response.status,
      errorData.code
    )
  }
  
  return response.json()
}

export interface OnboardingVerifyRequest {
  token: string
  employee_id: string
}

export interface OnboardingVerifyResponse {
  private_key: string
  employee_id: string
  role: 'worker' | 'admin'
  full_name: string
  short_id: number
  totp_secret: string
}

export interface SiteInfo {
  id: string
  name: string
  address: string | null
  is_active: boolean
}

export interface EmployeeSitesResponse {
  employee_id: string
  sites: SiteInfo[]
}

export interface WorkDayInfo {
  date: string
  is_work_day: boolean
  work_start: string | null
  work_end: string | null
  exception_type: string | null
  notes: string | null
}

export interface MonthScheduleResponse {
  employee_id: string
  month: number
  year: number
  work_start: string | null
  work_end: string | null
  late_tolerance_minutes: number
  days: WorkDayInfo[]
}

export class OnboardingApi {
  private static readonly REQUEST_TIMEOUT = 30000 // 30 секунд
  
  /**
   * Проверяет токен онбординга и получает приватный ключ
   */
  static async verifyToken(
    request: OnboardingVerifyRequest
  ): Promise<OnboardingVerifyResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), OnboardingApi.REQUEST_TIMEOUT)
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/worker/onboarding/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      return handleResponse<OnboardingVerifyResponse>(response)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Превышено время ожидания ответа сервера', 0, 'timeout')
      }
      throw error
    }
  }
}

export class WorkerApi {
  private static readonly REQUEST_TIMEOUT = 15000 // 15 секунд
  
  /**
   * Получает список объектов, к которым привязан сотрудник
   */
  static async getEmployeeSites(employeeId: string): Promise<EmployeeSitesResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), WorkerApi.REQUEST_TIMEOUT)
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/worker/employee/${employeeId}/sites`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      return handleResponse<EmployeeSitesResponse>(response, true)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Превышено время ожидания ответа сервера', 0, 'timeout')
      }
      throw error
    }
  }
  
  /**
   * Получает рабочий график сотрудника на месяц
   */
  static async getEmployeeSchedule(employeeId: string, year: number, month: number): Promise<MonthScheduleResponse> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), WorkerApi.REQUEST_TIMEOUT)
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/worker/employee/${employeeId}/schedule/${year}/${month}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      return handleResponse<MonthScheduleResponse>(response, true)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Превышено время ожидания ответа сервера', 0, 'timeout')
      }
      throw error
    }
  }
}

/**
 * Generic API client для Worker App
 */
export const apiClient = {
  async get<T>(path: string): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)
    
    // Проверяем, является ли это запросом к employee endpoint
    const isEmployeeRequest = path.includes('/worker/employee/')
    
    try {
      const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      return handleResponse<T>(response, isEmployeeRequest)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Превышено время ожидания ответа сервера', 0, 'timeout')
      }
      throw error
    }
  },

  async post<T>(path: string, data?: any): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)
    
    try {
      const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      })
      
      clearTimeout(timeoutId)
      return handleResponse<T>(response)
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError('Превышено время ожидания ответа сервера', 0, 'timeout')
      }
      throw error
    }
  }
}
